import { useEffect, useMemo } from "react";
import clsx from "clsx";

import { Button } from "../../../ui/index.ts";
import { PHASE2_DOC_SLOTS, type DocumentFilesState } from "../doc-types.ts";
import type { BankStepValues } from "../schemas/onboard-post-join.schema.ts";

import styles from "./review-step.module.css";

function BlobFileViewLink({ file }: { file: File }) {
  const url = useMemo(() => URL.createObjectURL(file), [file]);
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className={styles.viewLink}
    >
      View
    </a>
  );
}

export type ReviewStepProps = {
  bank: BankStepValues;
  cancelledCheque: File | null;
  documentFiles: DocumentFilesState;
  loading: boolean;
  onBack: () => void;
  onSubmit: () => void | Promise<void>;
  hideFooter?: boolean;
};

export function ReviewStep({
  bank,
  cancelledCheque,
  documentFiles,
  loading,
  onBack,
  onSubmit,
  hideFooter = false,
}: ReviewStepProps) {
  return (
    <div className={styles.root}>
      <p className={`body-sm ${styles.lead}`}>Review your details before submission.</p>
      <hr className={styles.separator} />
      <section className={styles.section} aria-labelledby="review-bank-heading">
        <h3 id="review-bank-heading" className="heading-xs">
          Bank details
        </h3>
        <dl className={styles.dl}>
          <div className={styles.dlItem}>
            <dt>Beneficiary Name</dt>
            <dd>{bank.beneficiaryName}</dd>
          </div>
          <div className={styles.dlItem}>
            <dt>Account Number</dt>
            <dd>{bank.accountNumber}</dd>
          </div>
          <div className={styles.dlItem}>
            <dt>Bank Name</dt>
            <dd>{bank.bankName}</dd>
          </div>
          <div className={styles.dlItem}>
            <dt>Branch Name</dt>
            <dd>{bank.branchName}</dd>
          </div>
          <div className={styles.dlItem}>
            <dt>IFSC Code</dt>
            <dd>{bank.ifscCode}</dd>
          </div>
          <div className={styles.dlItem}>
            <dt>Cancelled Cheque</dt>
            <dd className={styles.fileValue}>
              {cancelledCheque ? (
                <>
                  <BlobFileViewLink file={cancelledCheque} />
                  <span className={styles.fileName}>{cancelledCheque.name}</span>
                </>
              ) : (
                <span className={styles.fileNameMuted}>—</span>
              )}
            </dd>
          </div>
        </dl>
      </section>
      <hr className={styles.separator} />
      <section className={styles.section} aria-labelledby="review-docs-heading">
        <h3 id="review-docs-heading" className="heading-xs">
          Documents
        </h3>
        <ul className={styles.docList}>
          {PHASE2_DOC_SLOTS.map((slot) => {
            const f = documentFiles[slot.docType];
            return (
              <li key={slot.docType} className={clsx("body-sm", styles.docRow)}>
                <span className={styles.docLabel}>{slot.label}</span>
                <div className={styles.docMeta}>
                  {f ? (
                    <>
                      <BlobFileViewLink file={f} />
                      <span className={styles.docName}>{f.name}</span>
                    </>
                  ) : (
                    <span className={styles.docNameMuted}>
                      {slot.required ? "—" : "Not uploaded"}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </section>
      {hideFooter ? null : (
        <div className={styles.actions}>
          <Button type="button" variant="muted-outline" disabled={loading} onClick={onBack}>
            Back
          </Button>
          <Button type="button" disabled={loading} onClick={() => void onSubmit()}>
            {loading ? "Submitting…" : "Submit"}
          </Button>
        </div>
      )}
    </div>
  );
}
